import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Calendar, LogOut, Edit, ArrowLeft } from 'lucide-react';
import Button from '../components/UI/Button';
import Card from '../components/UI/Card';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import { formatMad } from '../utils/currency';

const ProfileScreen = () => {
  const { user, logout, isAuthenticated, loading } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('profileActiveTab') || 'profile';
  });
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(() => {
    return localStorage.getItem('profileIsEditing') === 'true';
  });
  const [editData, setEditData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetail, setShowBookingDetail] = useState(false);

  useEffect(() => {
    // Don't redirect if still loading auth state
    if (loading) return;

    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    if (user) {
      console.log(user);
      
      setEditData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || ''
      });
      
      if (activeTab === 'bookings') {
        fetchBookings();
      }
    }
  }, [isAuthenticated, user, activeTab, navigate, loading]);

  // Show loading spinner while checking auth status
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
          <p className="text-text-secondary font-sofia">Loading...</p>
        </div>
      </div>
    );
  }

  const fetchBookings = async () => {
    setIsLoading(true);
    try {
      const response = await api.getBookings();
      setBookings(response.results || []);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const updatedUser = await api.updateProfile(editData);
      showSuccess(t('profile.profileUpdated'));
      setIsEditing(false);
      localStorage.removeItem('profileIsEditing');

      // Update user context with new data
      // You might need to refresh the user data or update the context
      window.location.reload(); // Temporary solution to refresh user data
    } catch (error) {
      showError(`Failed to update profile: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      showError(t('profile.passwordMismatch'));
      return;
    }

    setIsLoading(true);
    try {
      await api.changePassword({
        old_password: passwordData.old_password,
        new_password: passwordData.new_password,
        new_password_confirm: passwordData.confirm_password
      });
      showSuccess(t('profile.passwordChanged'));
      setPasswordData({
        old_password: '',
        new_password: '',
        confirm_password: ''
      });
      setShowPasswordForm(false);
    } catch (error) {
      showError(`Failed to change password: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewBookingDetail = (booking) => {
    setSelectedBooking(booking);
    setShowBookingDetail(true);
  };

  const handleCloseBookingDetail = () => {
    setSelectedBooking(null);
    setShowBookingDetail(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('profileActiveTab', tab);
  };

  const handleEditToggle = () => {
    const newEditingState = !isEditing;
    setIsEditing(newEditingState);
    localStorage.setItem('profileIsEditing', newEditingState.toString());
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen   flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-sofia font-bold text-primary mb-4">
            Access Denied
          </h1>
          <p className="text-text-secondary font-sofia mb-6">
            Please log in to access your profile.
          </p>
          <Button onClick={() => navigate('/')}>
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen  ">
      {/* Hero Section */}
      <section className="relative h-64 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0  bg-black bg-opacity-60"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat mix-blend-overlay"
          style={{
            backgroundImage: `url('/assets/images/villa Baidane-01.jpg')`
          }}
        ></div>

        <div className="relative z-10 text-center text-white px-4">
          <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="h-10 w-10" />
          </div>
          <h1 className="text-4xl md:text-6xl font-sofia font-bold mb-4 animate-fade-in-up">
            {user.first_name} {user.last_name}
          </h1>
          <p className="text-lg md:text-xl font-sofia font-light animate-fade-in-up opacity-90" style={{animationDelay: '0.2s'}}>
            {user.email}
          </p>
        </div>
      </section>

      {/* Profile Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-text-secondary hover:text-primary transition-colors mb-8 font-sofia"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </button>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-8">
            <button
              onClick={() => handleTabChange('profile')}
              className={`flex-1 px-6 py-4 text-center font-sofia font-medium transition-colors ${
                activeTab === 'profile'
                  ? 'text-accent border-b-2 border-accent bg-accent bg-opacity-5'
                  : 'text-text-secondary hover:text-primary'
              }`}
            >
              <User className="h-5 w-5 inline-block mr-2" />
              {t('profile.personalInfo')}
            </button>
            <button
              onClick={() => handleTabChange('bookings')}
              className={`flex-1 px-6 py-4 text-center font-sofia font-medium transition-colors ${
                activeTab === 'bookings'
                  ? 'text-accent border-b-2 border-accent bg-accent bg-opacity-5'
                  : 'text-text-secondary hover:text-primary'
              }`}
            >
              <Calendar className="h-5 w-5 inline-block mr-2" />
              {t('profile.bookingHistory')}
            </button>
          </div>

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <Card className="animate-fade-in-up">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-sofia font-bold text-primary">
                    {t('profile.personalInfo')}
                  </h3>
                  <button
                    onClick={handleEditToggle}
                    className="flex items-center space-x-2 text-accent hover:text-accent-dark transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                    <span className="font-sofia">{isEditing ? t('profile.cancel') : t('profile.edit')}</span>
                  </button>
                </div>

                {isEditing ? (
                  <form onSubmit={handleUpdateProfile} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                          {t('profile.firstName')}
                        </label>
                        <input
                          type="text"
                          value={editData.first_name}
                          onChange={(e) => setEditData({...editData, first_name: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                          {t('profile.lastName')}
                        </label>
                        <input
                          type="text"
                          value={editData.last_name}
                          onChange={(e) => setEditData({...editData, last_name: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                        {t('profile.email')}
                      </label>
                      <input
                        type="email"
                        value={editData.email}
                        onChange={(e) => setEditData({...editData, email: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                        {t('profile.phone')}
                      </label>
                      <input
                        type="tel"
                        value={editData.phone}
                        onChange={(e) => setEditData({...editData, phone: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full"
                    >
                      {isLoading ? t('profile.updating') : t('profile.updateProfile')}
                    </Button>
                  </form>
                ) : (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <span className="text-sm text-text-secondary font-sofia">{t('profile.firstName')}</span>
                        <p className="font-sofia font-medium text-lg">{user.first_name}</p>
                      </div>
                      <div>
                        <span className="text-sm text-text-secondary font-sofia">{t('profile.lastName')}</span>
                        <p className="font-sofia font-medium text-lg">{user.last_name}</p>
                      </div>
                    </div>
                    
                    <div>
                      <span className="text-sm text-text-secondary font-sofia">{t('profile.email')}</span>
                      <p className="font-sofia font-medium text-lg">{user.email}</p>
                    </div>
                    
                    <div>
                      <span className="text-sm text-text-secondary font-sofia">{t('profile.phone')}</span>
                      <p className="font-sofia font-medium text-lg">{user.phone || t('profile.notProvided')}</p>
                    </div>
                    
                    <div>
                      <span className="text-sm text-text-secondary font-sofia">{t('profile.memberSince')}</span>
                      <p className="font-sofia font-medium text-lg">
                        {new Date(user.date_joined).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Change Password Section */}
                    <div className="pt-6 border-t border-gray-200">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-sofia font-semibold text-primary">{t('profile.security')}</h3>
                        <button
                          onClick={() => setShowPasswordForm(!showPasswordForm)}
                          className="text-accent hover:text-accent-dark font-sofia text-sm transition-colors"
                        >
                          {showPasswordForm ? t('profile.cancel') : t('profile.changePassword')}
                        </button>
                      </div>

                      {showPasswordForm && (
                        <form onSubmit={handleChangePassword} className="space-y-4">
                          <div>
                            <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                              {t('profile.currentPassword')}
                            </label>
                            <input
                              type="password"
                              value={passwordData.old_password}
                              onChange={(e) => setPasswordData({...passwordData, old_password: e.target.value})}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                              {t('profile.newPassword')}
                            </label>
                            <input
                              type="password"
                              value={passwordData.new_password}
                              onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                              {t('profile.confirmPassword')}
                            </label>
                            <input
                              type="password"
                              value={passwordData.confirm_password}
                              onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                              required
                            />
                          </div>

                          <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full"
                          >
                            {isLoading ? t('profile.changingPassword') : t('profile.changePassword')}
                          </Button>
                        </form>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <Card className="animate-fade-in-up">
              <div className="p-6">
                <h3 className="text-2xl font-sofia font-bold text-black mb-6">
                  {t('profile.bookingHistory')}
                </h3>
                
                {isLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
                    <p className="text-text-secondary font-sofia mt-4">{t('common.loading')}</p>
                  </div>
                ) : bookings.length > 0 ? (
                  <div className="space-y-6">
                    {bookings.map((booking) => (
                      <div key={booking.id} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="text-xl font-sofia font-semibold text-black">
                              {booking.villa_display_name}
                            </h4>
                            <p className="text-sm text-text-secondary font-sofia">
                              {t('profile.bookingId')}: {booking.booking_id || booking.id}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-sofia ${
                            booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {booking.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <span className="text-sm text-text-secondary font-sofia">Check-in:</span>
                            <p className="font-sofia font-medium text-black">{booking.check_in}</p>
                          </div>
                          <div>
                            <span className="text-sm text-text-secondary font-sofia">Check-out:</span>
                            <p className="font-sofia font-medium text-black">{booking.check_out}</p>
                          </div>
                          <div>
                            <span className="text-sm text-text-secondary font-sofia">Guests:</span>
                            <p className="font-sofia font-medium text-black">{booking.guests}</p>
                          </div>
                          <div>
                            <span className="text-sm text-text-secondary font-sofia">Total:</span>
                            <p className="font-sofia font-semibold text-[#81736d]">{formatMad(booking.total_amount)}</p>
                          </div>
                        </div>

                        {/* Booking Details */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-text-secondary font-sofia">{t('profile.guestName')}:</span>
                              <p className="font-sofia font-medium text-black">{booking.first_name} {booking.last_name}</p>
                            </div>
                            <div>
                              <span className="text-text-secondary font-sofia">{t('profile.email')}:</span>
                              <p className="font-sofia font-medium text-black">{booking.email}</p>
                            </div>
                            <div>
                              <span className="text-text-secondary font-sofia">{t('profile.phone')}:</span>
                              <p className="font-sofia font-medium text-black">{booking.phone}</p>
                            </div>
                            <div>
                              <span className="text-text-secondary font-sofia">{t('profile.nights')}:</span>
                              <p className="font-sofia font-medium text-black">{booking.nights} nights</p>
                            </div>
                            {booking.special_requests && (
                              <div className="md:col-span-2">
                                <span className="text-text-secondary font-sofia">{t('profile.specialRequests')}:</span>
                                <p className="font-sofia font-medium text-black">{booking.special_requests}</p>
                              </div>
                            )}
                          </div>

                          {/* View Details Button */}
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <Button
                              onClick={() => handleViewBookingDetail(booking)}
                              variant="outline"
                              size="sm"
                              className="w-full"
                            >
                              {t('profile.viewFullDetails')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-text-secondary font-sofia text-lg">{t('profile.noBookings')}</p>
                    <p className="text-sm text-text-secondary font-sofia mt-2 mb-6">
                      {t('profile.noBookingsDesc')}
                    </p>
                    <Button onClick={() => navigate('/booking')}>
                      {t('profile.makeBooking')}
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Booking Detail Modal */}
          {showBookingDetail && selectedBooking && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto text-text-primary">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-sofia font-bold text-black">
                      {t('profile.bookingDetails')}
                    </h3>
                    <button
                      onClick={handleCloseBookingDetail}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label={t('profile.close')}
                    >
                      ✕
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* Booking ID and Status */}
                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm text-text-secondary font-sofia">{t('profile.bookingId')}</p>
                        <p className="text-lg font-sofia font-semibold text-black">
                          {selectedBooking.booking_id || selectedBooking.id}
                        </p>
                      </div>
                      <span className={`px-4 py-2 rounded-full text-sm font-sofia capitalize ${
                        selectedBooking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                        selectedBooking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        selectedBooking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedBooking.status}
                      </span>
                    </div>

                    {/* Villa Information */}
                    <div>
                      <h4 className="text-lg font-sofia font-semibold text-black mb-3">{t('profile.villaInfo')}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <span className="text-sm text-text-secondary font-sofia">{t('profile.villa')}</span>
                          <p className="font-sofia font-medium text-black">{selectedBooking.villa_display_name}</p>
                        </div>
                        <div>
                          <span className="text-sm text-text-secondary font-sofia">{t('profile.guests')}</span>
                          <p className="font-sofia font-medium text-black">{selectedBooking.guests} guests</p>
                        </div>
                      </div>
                    </div>

                    {/* Stay Information */}
                    <div>
                      <h4 className="text-lg font-sofia font-semibold text-black mb-3">{t('profile.stayInfo')}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <span className="text-sm text-text-secondary font-sofia">{t('profile.checkIn')}</span>
                          <p className="font-sofia font-medium text-black">{selectedBooking.check_in}</p>
                        </div>
                        <div>
                          <span className="text-sm text-text-secondary font-sofia">{t('profile.checkOut')}</span>
                          <p className="font-sofia font-medium text-black">{selectedBooking.check_out}</p>
                        </div>
                        <div>
                          <span className="text-sm text-text-secondary font-sofia">{t('profile.nights')}</span>
                          <p className="font-sofia font-medium text-black">{selectedBooking.nights} nights</p>
                        </div>
                      </div>
                    </div>

                    {/* Guest Information */}
                    <div>
                      <h4 className="text-lg font-sofia font-semibold text-black mb-3">{t('profile.guestInfo')}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <span className="text-sm text-text-secondary font-sofia">{t('profile.name')}</span>
                          <p className="font-sofia font-medium text-black">{selectedBooking.first_name} {selectedBooking.last_name}</p>
                        </div>
                        <div>
                          <span className="text-sm text-text-secondary font-sofia">{t('profile.email')}</span>
                          <p className="font-sofia font-medium text-black">{selectedBooking.email}</p>
                        </div>
                        <div>
                          <span className="text-sm text-text-secondary font-sofia">{t('profile.phone')}</span>
                          <p className="font-sofia font-medium text-black">{selectedBooking.phone}</p>
                        </div>
                      </div>
                    </div>

                    {/* Pricing */}
                    <div>
                      <h4 className="text-lg font-sofia font-semibold text-black mb-3">{t('profile.pricing')}</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="font-sofia text-text-secondary">{t('profile.basePrice')}</span>
                          <span className="font-sofia font-medium text-black">{formatMad(selectedBooking.base_price)}</span>
                        </div>
                        {selectedBooking.services_total > 0 && (
                          <div className="flex justify-between">
                            <span className="font-sofia text-text-secondary">{t('profile.services')}</span>
                            <span className="font-sofia font-medium text-black">{formatMad(selectedBooking.services_total)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-lg font-semibold border-t border-gray-200 pt-2">
                          <span className="font-sofia text-black">{t('profile.total')}</span>
                          <span className="font-sofia text-[#81736d]">{formatMad(selectedBooking.total_amount)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Special Requests */}
                    {selectedBooking.special_requests && (
                      <div>
                        <h4 className="text-lg font-sofia font-semibold text-black mb-3">{t('profile.specialRequests')}</h4>
                        <p className="font-sofia text-black bg-gray-50 p-4 rounded-lg">{selectedBooking.special_requests}</p>
                      </div>
                    )}

                    {/* Booking Date */}
                    <div>
                      <h4 className="text-lg font-sofia font-semibold text-black mb-3">{t('profile.bookingDate')}</h4>
                      <p className="font-sofia text-black">
                        {new Date(selectedBooking.created_at).toLocaleDateString()} at {new Date(selectedBooking.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <Button
                      onClick={handleCloseBookingDetail}
                      className="w-full"
                    >
                      {t('profile.close')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Logout Button */}
          <div className="mt-8">
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full flex items-center justify-center space-x-2 text-red-600 border-red-600 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" />
              <span>{t('profile.signOut')}</span>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProfileScreen;
